/**
 * Composer seeds, the gate in front of them, and recap identity
 * (F-JR-20, F-JR-21, F-JR-22).
 *
 * Every writing route in the product — Write, a prompt, Continue writing, a row
 * tap, a goal reflection, a workout recap — funnels through **one** seed, and
 * therefore through one set of gating rules and one composer. That is the whole
 * point of F-JR-19, and it only holds if the seed is built somewhere no
 * component can bypass. So this module is pure: it takes what a route knows and
 * returns either a refusal sentence or a working entry, and it reads no clock,
 * no repository and no React.
 *
 * The revision arithmetic is the subtle half. `workingRevision` is reserved
 * **up front** rather than computed at save time, so that however many
 * autosaves happen in between, promotion still records exactly one increment —
 * which is the only shape the repository's `current + 1` rule accepts.
 */

import type { EffortSessionId } from "@ff/domain";
import type {
  JournalBlockId,
  JournalDraft,
  JournalEntry,
  JournalEntryId,
  JournalEntryKind,
  JournalGoalId,
  JournalGoalLink,
  JournalInstant,
  JournalPerceivedEffort,
  JournalPromptReference,
  JournalRecapRequest,
  JournalRecapResolution,
  JournalRevision,
  JournalTextBlock,
  JournalVault,
  JournalComposerOrigin,
  JournalComposerSeed,
} from "@ff/domain";
import {
  JOURNAL_ENTRY_SCHEMA_VERSION,
  JOURNAL_EMPTY_DOCUMENT,
  JOURNAL_FIRST_REVISION,
  JOURNAL_LIBRARY_COPY,
  JOURNAL_PERCEIVED_EFFORT_RANGE,
  JOURNAL_RECAP_COPY,
  assembleJournalDocument,
  journalDocumentPlainText,
  isJournalTextBlock,
  journalRunPlainText,
  nextJournalRevision,
} from "@ff/domain";

/**
 * Ids the composer mints.
 *
 * Injected rather than taken from `crypto`, for the same reason the editor
 * injects `nextBlockId`: a feature that reached for a browser global would be a
 * feature no test could pin.
 *
 * The names are the composer's, not the port's — this interface says what is
 * about to be created (`nextBlockId`) where `@ff/domain`'s `IdPort` says what
 * kind of identity is minted (`journalBlockId`). `app-state/identity.ts` is the
 * one place the two vocabularies meet.
 */
export interface JournalComposerIdentity {
  readonly nextEntryId: () => JournalEntryId;
  readonly nextBlockId: () => JournalBlockId;
  /** F-JR-31's inline goal creation mints one of these. */
  readonly nextGoalId: () => JournalGoalId;
}

/**
 * F-JR-21: a recap's entry id **is** its session id.
 *
 * The two brands are deliberately different everywhere else — an effort id and
 * a journal id are not interchangeable — so the one place the product says they
 * are the same value is written once, here, rather than at each call site where
 * a reader would have to work out whether it was a mistake.
 */
export function journalRecapEntryId(sessionID: EffortSessionId): JournalEntryId {
  return sessionID as string as JournalEntryId;
}

/* ------------------------------------------------------------------------- *
 * F-JR-20 — the gate
 * ------------------------------------------------------------------------- */

export type JournalComposerGate =
  | { readonly kind: "open" }
  | { readonly kind: "refused"; readonly message: string };

/**
 * No composer may open until the library is verified ready: ready **and**
 * available **and** not loading.
 *
 * The vault is the authority because it is the only value in the app that can
 * hold a library at all — `revealed` is the single state with entries in it, so
 * "verified ready" and "the vault is revealed" are the same fact rather than two
 * that could disagree. `concealed` refuses with the still-opening sentence, not
 * the unavailable one: nothing is broken, the user simply has not been through
 * the curtain yet, and telling them their journal is unavailable would be false.
 */
export function journalComposerGate(vault: JournalVault): JournalComposerGate {
  switch (vault.state) {
    case "revealed":
      return { kind: "open" };
    case "unavailable":
      return { kind: "refused", message: JOURNAL_LIBRARY_COPY.repositoryUnavailable };
    case "preparing":
    case "authenticating":
    case "concealed":
      return { kind: "refused", message: JOURNAL_LIBRARY_COPY.stillOpening };
  }
}

/* ------------------------------------------------------------------------- *
 * F-JR-21 — seeds
 * ------------------------------------------------------------------------- */

export interface BlankSeedInput {
  readonly entryKind?: JournalEntryKind;
  readonly prompt?: JournalPromptReference;
  readonly goal?: JournalGoalLink;
  readonly occurredAt: JournalInstant;
}

/**
 * `.blank(kind:prompt:goal:occurredAt:)` — kind defaults to `.general`,
 * `workingRevision` is 1, and the id is new.
 */
export function blankComposerSeed(
  input: BlankSeedInput,
  identity: JournalComposerIdentity,
): JournalComposerSeed {
  return {
    kind: "blank",
    entryId: identity.nextEntryId(),
    entryKind: input.entryKind ?? "general",
    ...(input.prompt === undefined ? {} : { prompt: input.prompt }),
    ...(input.goal === undefined ? {} : { goal: input.goal }),
    occurredAt: input.occurredAt,
    workingRevision: JOURNAL_FIRST_REVISION,
  };
}

/**
 * `.editing(entry)` reserves `revision + 1`, saturating.
 *
 * `nextJournalRevision` is used rather than `+ 1` precisely because it
 * saturates: a wrapped revision compares as *older* than the record it replaces,
 * and the repository would then refuse every later write to that entry for the
 * life of the file.
 */
export function editingComposerSeed(entry: JournalEntry): JournalComposerSeed {
  return { kind: "editing", entry, workingRevision: nextJournalRevision(entry.revision) };
}

/** `.resuming(draft)` keeps the revision the draft already reserved. */
export function resumingComposerSeed(draft: JournalDraft): JournalComposerSeed {
  return { kind: "resuming", draft, workingRevision: draft.entry.revision };
}

/**
 * `.workoutRecap(request)` — the session UUID **is** the entry id, which is
 * what makes a recap stable across every route that can reach it.
 */
export function workoutRecapComposerSeed(
  request: JournalRecapRequest,
): JournalComposerSeed {
  return {
    kind: "workoutRecap",
    request,
    entryId: journalRecapEntryId(request.sessionID),
    workingRevision: JOURNAL_FIRST_REVISION,
  };
}

/** `.merging(request, existing)` — the merge itself is in `composerSeedEntry`. */
export function mergingComposerSeed(
  request: JournalRecapRequest,
  existing: JournalEntry,
): JournalComposerSeed {
  return {
    kind: "merging",
    request,
    existing,
    workingRevision: nextJournalRevision(existing.revision),
  };
}

/** Which discard rules apply on close (F-JR-25). */
export function composerSeedOrigin(seed: JournalComposerSeed): JournalComposerOrigin {
  switch (seed.kind) {
    case "blank":
    case "workoutRecap":
      return "new";
    case "resuming":
      return "draft";
    case "editing":
    case "merging":
      return "persisted";
  }
}

/** The record the seed was built from, when there is one. */
export function composerSeedOriginEntry(
  seed: JournalComposerSeed,
): JournalEntry | undefined {
  switch (seed.kind) {
    case "editing":
      return seed.entry;
    case "resuming":
      return seed.draft.entry;
    case "merging":
      return seed.existing;
    case "blank":
    case "workoutRecap":
      return undefined;
  }
}

export interface ComposerSeedContext {
  /** Wall reading for the fields a new record has to stamp. */
  readonly now: JournalInstant;
  readonly identity: JournalComposerIdentity;
}

/**
 * The working entry a seed opens on.
 *
 * Note what is *not* here: no clock is read, no id is minted outside
 * `identity`, and nothing is written. The composer's whole first frame is a
 * pure function of the seed, which is what lets the recap-identity rules below
 * be tested without a repository.
 */
export function composerSeedEntry(
  seed: JournalComposerSeed,
  context: ComposerSeedContext,
): JournalEntry {
  switch (seed.kind) {
    case "blank":
      return {
        schemaVersion: JOURNAL_ENTRY_SCHEMA_VERSION,
        id: seed.entryId,
        revision: seed.workingRevision,
        occurredAt: seed.occurredAt,
        createdAt: context.now,
        updatedAt: context.now,
        document: JOURNAL_EMPTY_DOCUMENT,
        kind: seed.entryKind,
        isFavorite: false,
        tags: [],
        ...(seed.prompt === undefined ? {} : { prompt: seed.prompt }),
        ...(seed.goal === undefined ? {} : { goal: seed.goal }),
        attachments: [],
      };
    case "editing":
      return { ...seed.entry, revision: seed.workingRevision };
    case "resuming":
      return { ...seed.draft.entry, revision: seed.workingRevision };
    case "workoutRecap":
      return recapEntry(seed.request, seed.entryId, seed.workingRevision, context);
    case "merging":
      return mergeRecap(seed.request, seed.existing, seed.workingRevision, context);
  }
}

/**
 * A brand-new recap: the effort embed first, then the optional initial note.
 *
 * `occurredAt` is the session's start rather than now — the entry is *about*
 * the workout, and back-dating it here is what keeps the timeline honest when
 * the recap is written days later.
 */
function recapEntry(
  request: JournalRecapRequest,
  entryId: JournalEntryId,
  revision: JournalRevision,
  context: ComposerSeedContext,
): JournalEntry {
  const note = trimmedNote(request.initialNote);
  const textBlocks: JournalTextBlock[] =
    note === undefined
      ? []
      : [
          {
            kind: "text",
            id: context.identity.nextBlockId(),
            style: "paragraph",
            nestingLevel: 0,
            runs: [{ text: note, marks: [] }],
          },
        ];

  const perceivedEffort = clampPerceivedEffort(request.perceivedEffort);

  return {
    schemaVersion: JOURNAL_ENTRY_SCHEMA_VERSION,
    id: entryId,
    revision,
    occurredAt: request.snapshot.startedAt,
    createdAt: context.now,
    updatedAt: context.now,
    document: assembleJournalDocument({
      effort: {
        blockId: context.identity.nextBlockId(),
        link: { sessionID: request.sessionID, snapshot: request.snapshot },
      },
      textBlocks,
      media: [],
    }),
    kind: "workoutRecap",
    isFavorite: false,
    tags: [],
    attachments: [],
    effort: { sessionID: request.sessionID, snapshot: request.snapshot },
    ...(perceivedEffort === undefined
      ? {}
      : { workoutReflection: { perceivedEffort, bodyObservation: "" } }),
  };
}

/**
 * F-JR-21's merge: **existing wins** on perceived effort, the existing body
 * observation is kept, and the initial note is appended only when no text block
 * already says exactly that.
 *
 * The "already says it" check is what makes reopening a recap idempotent. A
 * summary that hands the same note over twice — because the user tapped Write
 * full recap again — must not stack two identical paragraphs into the entry.
 */
function mergeRecap(
  request: JournalRecapRequest,
  existing: JournalEntry,
  revision: JournalRevision,
  context: ComposerSeedContext,
): JournalEntry {
  const note = trimmedNote(request.initialNote);
  const alreadyPresent =
    note === undefined ||
    existing.document.blocks
      .filter(isJournalTextBlock)
      .some((block) => journalRunPlainText(block.runs) === note);

  const document =
    alreadyPresent || note === undefined
      ? existing.document
      : {
          ...existing.document,
          blocks: [
            ...existing.document.blocks,
            {
              kind: "text",
              id: context.identity.nextBlockId(),
              style: "paragraph",
              nestingLevel: 0,
              runs: [{ text: note, marks: [] }],
            } satisfies JournalTextBlock,
          ],
        };

  const base = { ...existing, revision, document };
  const effort = existing.effort;
  if (effort === undefined) {
    return { ...base, effort: undefined, workoutReflection: undefined };
  }

  // Existing wins. The incoming score is the summary's *current* reading, and a
  // recap the user already scored must not be re-scored by reopening it.
  const perceivedEffort =
    existing.workoutReflection?.perceivedEffort ?? clampPerceivedEffort(request.perceivedEffort);
  if (perceivedEffort === undefined) {
    return { ...base, effort, workoutReflection: undefined };
  }

  return {
    ...base,
    effort,
    workoutReflection: {
      perceivedEffort,
      // Kept verbatim: a body observation is the user's own sentence about pain,
      // and no merge has anything to replace it with.
      bodyObservation: existing.workoutReflection?.bodyObservation ?? "",
    },
  };
}

/** Trimmed; empty collapses to absent, which is what the record stores. */
function trimmedNote(note: string | undefined): string | undefined {
  const trimmed = note?.trim();
  return trimmed === undefined || trimmed.length === 0 ? undefined : trimmed;
}

/** F-JR-22: perceived effort clamps to 1…10 rather than being refused. */
function clampPerceivedEffort(
  value: number | undefined,
): JournalPerceivedEffort | undefined {
  if (value === undefined) return undefined;
  const { min, max } = JOURNAL_PERCEIVED_EFFORT_RANGE;
  return Math.min(max, Math.max(min, Math.round(value))) as JournalPerceivedEffort;
}

/* ------------------------------------------------------------------------- *
 * F-JR-22 — recap identity and collisions
 * ------------------------------------------------------------------------- */

export interface RecapResolutionInput {
  readonly request: JournalRecapRequest;
  readonly entries: readonly JournalEntry[];
  readonly drafts: readonly JournalDraft[];
}

/**
 * Resolution order, and it is an order rather than a search: a draft that owns
 * the session's *identity* but carries a different effort link is a genuine
 * collision and is refused, because merging would overwrite one workout's notes
 * with another's.
 *
 * Matching is on `effort.sessionID`, not on the entry id, so a legacy recap
 * written under a non-canonical UUID resumes instead of silently duplicating.
 */
export function resolveJournalRecap(
  input: RecapResolutionInput,
): JournalRecapResolution {
  const { request } = input;
  const recapId = journalRecapEntryId(request.sessionID);

  const draftOwningIdentity = input.drafts.find((draft) => draft.id === recapId);
  if (
    draftOwningIdentity !== undefined &&
    draftOwningIdentity.entry.effort?.sessionID !== request.sessionID
  ) {
    return { kind: "refusedByDraft" };
  }

  const preferredDraft = preferred(
    input.drafts.filter((draft) => draft.entry.effort?.sessionID === request.sessionID),
    recapId,
    (draft) => draft.id,
    (draft) => draft.savedAt,
  );
  if (preferredDraft !== undefined) {
    return { kind: "resumeDraft", draft: preferredDraft };
  }

  const entryOwningIdentity = input.entries.find((entry) => entry.id === recapId);
  if (
    entryOwningIdentity !== undefined &&
    entryOwningIdentity.effort?.sessionID !== request.sessionID
  ) {
    return { kind: "refusedByEntry" };
  }

  const preferredEntry = preferred(
    input.entries.filter((entry) => entry.effort?.sessionID === request.sessionID),
    recapId,
    (entry) => entry.id,
    (entry) => entry.updatedAt,
  );
  if (preferredEntry !== undefined) {
    return { kind: "editEntry", entry: preferredEntry };
  }

  return { kind: "newRecap" };
}

/**
 * F-JR-22's preference ordering: the canonical id first, then the newest
 * `savedAt`/`updatedAt`, then `uuidString`.
 *
 * The final tie-break on the id string is not decoration — without it the
 * choice between two records saved in the same millisecond depends on array
 * order, and array order here is whatever the file enumeration happened to
 * return.
 */
function preferred<T>(
  candidates: readonly T[],
  canonicalId: JournalEntryId,
  idOf: (candidate: T) => JournalEntryId,
  timestampOf: (candidate: T) => JournalInstant,
): T | undefined {
  return [...candidates].sort((left, right) => {
    const leftCanonical = idOf(left) === canonicalId;
    const rightCanonical = idOf(right) === canonicalId;
    if (leftCanonical !== rightCanonical) return leftCanonical ? -1 : 1;
    const timeDelta = timestampOf(right) - timestampOf(left);
    if (timeDelta !== 0) return timeDelta;
    return idOf(left) < idOf(right) ? -1 : idOf(left) > idOf(right) ? 1 : 0;
  })[0];
}

/** The sentence a refused recap prints. */
export function recapRefusalMessage(
  resolution: JournalRecapResolution,
): string | undefined {
  switch (resolution.kind) {
    case "refusedByDraft":
      return JOURNAL_RECAP_COPY.refusedByDraft;
    case "refusedByEntry":
      return JOURNAL_RECAP_COPY.refusedByEntry;
    case "resumeDraft":
    case "editEntry":
    case "newRecap":
      return undefined;
  }
}

/* ------------------------------------------------------------------------- *
 * Blankness and initial dirtiness (F-JR-23)
 * ------------------------------------------------------------------------- */

/**
 * A blank entry: no title, no body text, no attachments, and none of the
 * context fields a user can only reach deliberately.
 *
 * The effort link is deliberately **not** consulted. A recap opened and closed
 * without a word typed still has its embed, and treating that as content would
 * make "discard a blank new entry" unreachable for the one route that needs it
 * most.
 */
export function journalComposerIsBlank(entry: JournalEntry): boolean {
  if ((entry.title ?? "").trim().length > 0) return false;
  if (journalDocumentPlainText(entry.document).trim().length > 0) return false;
  if (entry.attachments.length > 0) return false;
  if (entry.tags.length > 0) return false;
  if (entry.feeling !== undefined) return false;
  if (entry.energy !== undefined) return false;
  if (entry.goal !== undefined) return false;
  if (entry.prompt !== undefined) return false;
  if (entry.isFavorite) return false;
  if (entry.workoutReflection !== undefined) return false;
  return true;
}

function withRevisionOf(origin: JournalEntry, next: JournalEntry): JournalEntry {
  return { ...next, revision: origin.revision };
}

/**
 * Whether a seed opens **dirty** — F-JR-23's "seeds start dirty when the seed
 * differs from its origin, or for a new seed with any non-default field".
 *
 * A recap is the reason this rule exists. It carries an effort embed, a
 * back-dated `occurredAt` and possibly a note and an RPE, none of which the user
 * typed; if the composer opened `untouched`, closing it would preserve nothing
 * and the workout the summary just handed over would be gone.
 */
export function composerSeedIsDirty(
  seed: JournalComposerSeed,
  working: JournalEntry,
  sameAsOrigin: (origin: JournalEntry, next: JournalEntry) => boolean,
): boolean {
  switch (seed.kind) {
    case "blank":
      // A blank seed with a prompt, a goal or a non-general kind already holds
      // a decision worth preserving.
      return !journalComposerIsBlank(working) || seed.entryKind !== "general";
    case "workoutRecap":
      return true;
    // The reserved revision is normalized away before comparing. It always
    // differs from the origin's by construction, and a seed that counted it as
    // a change would open every edit dirty — which would make "close without
    // saving" offer to discard changes nobody made.
    case "editing":
      return !sameAsOrigin(seed.entry, withRevisionOf(seed.entry, working));
    case "resuming":
      return !sameAsOrigin(seed.draft.entry, withRevisionOf(seed.draft.entry, working));
    case "merging":
      return !sameAsOrigin(seed.existing, withRevisionOf(seed.existing, working));
  }
}
